import { create } from "zustand";
import { devtools } from "zustand/middleware";

export type NetworkStatus =
  | "online"
  | "unstable"
  | "offline"
  | "services-unavailable";

type ServiceHealth = {
  api: boolean;
  firebase: boolean;
  auth: boolean;
  functions: boolean;
  firebaseStorage: boolean;
  notifications: boolean;
  storage: boolean;
  stripe: boolean;
};

export type ConnectionQuality = "fast" | "slow";

type State = {
  status: NetworkStatus;
  serviceHealth: ServiceHealth;
  failedRequestsCount: number;
  lastCheckAt: number | null;
  // Stripe is only relevant in payment flows. The flag is flipped by pages
  // that actually load Stripe.js so we don't probe js.stripe.com for the
  // 99% of sessions that never touch a payment screen.
  stripeCheckEnabled: boolean;
  connectionQuality: ConnectionQuality;
};

type Action = {
  setStatus: (status: NetworkStatus) => void;
  reportFailedRequest: () => void;
  reportSuccessRequest: () => void;
  checkConnection: () => Promise<boolean>;
  checkServices: () => Promise<ServiceHealth>;
  setStripeCheckEnabled: (enabled: boolean) => void;
  setConnectionQuality: (quality: ConnectionQuality) => void;
};

const FAILED_REQUESTS_THRESHOLD = 3;

// Set once a full-page navigation has started. Firefox cancels the
// document's in-flight fetches at that moment, while the page is still
// rendered, so probe and request failures reported after this are
// navigation artefacts, not outages. A started navigation can still be
// abandoned (cancelled load, "stay on page" answer, download response)
// without the document ever unloading, hence the timed reset.
const UNLOADING_RESET_MS = 15_000;
let isUnloading = false;
if (typeof window !== "undefined") {
  let resetTimer: ReturnType<typeof setTimeout>;
  const markUnloading = () => {
    isUnloading = true;
    clearTimeout(resetTimer);
    resetTimer = setTimeout(() => {
      isUnloading = false;
    }, UNLOADING_RESET_MS);
  };
  window.addEventListener("beforeunload", markUnloading);
  window.addEventListener("pagehide", markUnloading);
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) isUnloading = false;
  });
}

// Apps served from *.ffair.show route Firebase traffic through a
// reverse proxy (proxy.ffair.show) instead of calling Google APIs
// directly. In that case the single dependency is the proxy — we ping
// its health endpoint and use that one result for all Firebase services.
const isProxyMode = (): boolean =>
  window.location.hostname.endsWith("ffair.show");

const pingProxy = async (): Promise<boolean> => {
  try {
    // HEAD — we only care about the status line, not the body. Express and
    // nginx route HEAD through the GET handler by default; if the backend
    // ever returns 405 we still consider the server alive (any response
    // under 500 counts).
    const response = await fetch("https://proxy.ffair.show/health", {
      method: "HEAD",
      cache: "no-store",
      signal: AbortSignal.timeout(10_000),
    });
    return response.status < 500;
  } catch {
    return false;
  }
};

const pingInternet = async (): Promise<boolean> => {
  try {
    await fetch(`${window.location.origin}/robots.txt?_=${Date.now()}`, {
      method: "HEAD",
      cache: "no-store",
      signal: AbortSignal.timeout(5_000),
    });
    return true;
  } catch {
    return false;
  }
};

const pingApi = async (): Promise<boolean> => {
  try {
    const baseURL = import.meta.env.VITE_API_V2_URL as string;
    if (!baseURL) return true;

    const response = await fetch(`${baseURL.replace(/\/$/, "")}/health`, {
      method: "HEAD",
      cache: "no-store",
      signal: AbortSignal.timeout(10_000),
    });
    // 5xx = server is broken. Network error (catch) = server unreachable.
    // Anything else (200, 301, 404, 405) = server is alive and responding.
    return response.status < 500;
  } catch {
    return false;
  }
};

const pingFirebase = async (): Promise<boolean> => {
  try {
    // Reach Firestore's HTTP endpoint without performing a document read —
    // a real `.get()` is a billed operation and at ~120 reads/h per idle
    // client would add up fast across the user base. HEAD + no-cors to the
    // API root resolves once Google's infrastructure answers (any status);
    // network errors reject.
    await fetch("https://firestore.googleapis.com/", {
      method: "HEAD",
      mode: "no-cors",
      cache: "no-store",
      signal: AbortSignal.timeout(10_000),
    });
    return true;
  } catch {
    return false;
  }
};

const pingFunctions = async (): Promise<boolean> => {
  try {
    const projectId = import.meta.env.VITE_PROJECT_ID as string;
    if (!projectId) return true;

    // Firebase JS SDK defaults to the us-central1 region. A request to a
    // non-existent function name is answered by Google's load balancer with
    // a 404 without ever spinning up a function — so this is free (no
    // invocation is billed).
    //
    // `mode: "no-cors"` skips CORS preflight; the opaque response is enough
    // to confirm the infrastructure is reachable. Method must stay one of
    // GET/HEAD/POST — the fetch spec rejects other methods in no-cors mode.
    await fetch(
      `https://us-central1-${projectId}.cloudfunctions.net/__health_probe__`,
      {
        method: "HEAD",
        mode: "no-cors",
        cache: "no-store",
        signal: AbortSignal.timeout(10_000),
      }
    );
    return true;
  } catch {
    return false;
  }
};

const pingStripe = async (): Promise<boolean> => {
  try {
    // Stripe.js is served from js.stripe.com. If this host is blocked
    // (corporate firewall, ad-blocker), the SDK can't load and card
    // payments break. HEAD + no-cors lets us confirm connectivity without
    // actually downloading or executing the script.
    await fetch("https://js.stripe.com/v3/", {
      method: "HEAD",
      mode: "no-cors",
      cache: "no-store",
      signal: AbortSignal.timeout(10_000),
    });
    return true;
  } catch {
    return false;
  }
};

const pingFirebaseAuth = async (): Promise<boolean> => {
  try {
    // Firebase Auth SDK talks to `identitytoolkit.googleapis.com` for all
    // sign-in / token operations (the configured `authDomain` is only used
    // for OAuth redirect/popup flows). A request to the API root is answered
    // by Google's infrastructure with an error payload (404/400) without
    // triggering any billable operation or mutating state.
    await fetch("https://identitytoolkit.googleapis.com/", {
      method: "HEAD",
      mode: "no-cors",
      cache: "no-store",
      signal: AbortSignal.timeout(10_000),
    });
    return true;
  } catch {
    return false;
  }
};

const pingFirebaseStorage = async (): Promise<boolean> => {
  try {
    // HEAD to the API root — avoids the Class A "list objects" operation
    // that `/v0/b/{bucket}/o` would incur. The infrastructure answers with
    // a non-2xx status but an opaque response still proves connectivity.
    await fetch("https://firebasestorage.googleapis.com/", {
      method: "HEAD",
      mode: "no-cors",
      cache: "no-store",
      signal: AbortSignal.timeout(10_000),
    });
    return true;
  } catch {
    return false;
  }
};

const pingNotifications = async (): Promise<boolean> => {
  try {
    const wsUrl = import.meta.env.VITE_NOTIFICATION_SERVICE_URL as string;
    const wsPath = import.meta.env.VITE_NOTIFICATION_SERVICE_PATH as string;
    if (!wsUrl) return true;

    // Convert ws(s):// to http(s):// and append the service path
    const httpUrl = wsUrl
      .replace(/^ws(s?):\/\//, "http$1://")
      .replace(/\/$/, "");
    // Strip the trailing `socket.io` segment (with or without its leading
    // slash) so the health endpoint lands at the service root, not at
    // `/socket.io/health`.
    const normalizedPath = (wsPath ?? "")
      .replace(/\/?socket\.io\/?$/, "")
      .replace(/^\/+|\/+$/g, "");
    const basePath = normalizedPath ? `/${normalizedPath}` : "";
    await fetch(`${httpUrl}${basePath}/health`, {
      method: "HEAD",
      cache: "no-store",
      signal: AbortSignal.timeout(10_000),
    });
    return true;
  } catch {
    return false;
  }
};

const pingStorage = async (): Promise<boolean> => {
  try {
    const storageUrl = import.meta.env.VITE_STORAGE_SERVICE_HTTP_URL as string;
    if (!storageUrl || import.meta.env.MODE === "development") return true;

    await fetch(`${storageUrl.replace(/\/$/, "")}/health`, {
      method: "HEAD",
      cache: "no-store",
      signal: AbortSignal.timeout(10_000),
    });
    return true;
  } catch {
    return false;
  }
};

export const useNetworkStatus = create(
  devtools<State & Action>((set, get) => ({
    status: navigator.onLine ? "online" : "offline",
    serviceHealth: {
      api: true,
      firebase: true,
      auth: true,
      functions: true,
      firebaseStorage: true,
      notifications: true,
      storage: true,
      stripe: true,
    },
    failedRequestsCount: 0,
    lastCheckAt: null,
    stripeCheckEnabled: false,
    connectionQuality: "fast",

    setStatus: (status) => {
      set({ status, lastCheckAt: Date.now() });
    },

    setStripeCheckEnabled: (enabled) => {
      set({ stripeCheckEnabled: enabled });
    },

    setConnectionQuality: (quality) => {
      set({ connectionQuality: quality });
    },

    reportFailedRequest: () => {
      if (isUnloading) return;
      const { failedRequestsCount, status } = get();
      const next = failedRequestsCount + 1;

      if (next >= FAILED_REQUESTS_THRESHOLD && status === "online") {
        set({
          failedRequestsCount: next,
          status: "unstable",
          lastCheckAt: Date.now(),
        });
      } else {
        set({ failedRequestsCount: next });
      }
    },

    reportSuccessRequest: () => {
      const { status } = get();
      if (status === "unstable" || status === "services-unavailable") {
        set({
          failedRequestsCount: 0,
          status: "online",
          serviceHealth: {
            api: true,
            firebase: true,
            auth: true,
            functions: true,
            firebaseStorage: true,
            notifications: true,
            storage: true,
            stripe: true,
          },
          lastCheckAt: Date.now(),
        });
      } else if (get().failedRequestsCount > 0) {
        set({ failedRequestsCount: 0 });
      }
    },

    checkServices: async () => {
      // If the browser already knows we're offline, skip service pings
      if (!navigator.onLine) {
        const health: ServiceHealth = {
          api: false,
          firebase: false,
          auth: false,
          functions: false,
          firebaseStorage: false,
          notifications: false,
          storage: false,
          stripe: false,
        };
        set({
          serviceHealth: health,
          status: "offline",
          lastCheckAt: Date.now(),
        });
        return health;
      }

      let health: ServiceHealth;
      const { stripeCheckEnabled } = get();
      // Only probe Stripe when a payment flow has opted in. For other
      // sessions we assume reachable so the status list doesn't light up
      // red for a dependency the user will never touch.
      const maybePingStripe = stripeCheckEnabled
        ? pingStripe()
        : Promise.resolve(true);

      if (isProxyMode()) {
        // All Firebase traffic is tunneled through proxy.ffair.show —
        // collapse firebase/auth/functions/firebaseStorage into a single
        // proxy ping so we don't misreport "unavailable" for hosts that
        // are intentionally blocked in this deployment.
        const [api, proxy, notifications, storage, stripe] = await Promise.all([
          pingApi(),
          pingProxy(),
          pingNotifications(),
          pingStorage(),
          maybePingStripe,
        ]);
        health = {
          api,
          firebase: proxy,
          auth: proxy,
          functions: proxy,
          firebaseStorage: proxy,
          notifications,
          storage,
          stripe,
        };
      } else {
        const [
          api,
          firebase,
          auth,
          functions,
          firebaseStorage,
          notifications,
          storage,
          stripe,
        ] = await Promise.all([
          pingApi(),
          pingFirebase(),
          pingFirebaseAuth(),
          pingFunctions(),
          pingFirebaseStorage(),
          pingNotifications(),
          pingStorage(),
          maybePingStripe,
        ]);
        health = {
          api,
          firebase,
          auth,
          functions,
          firebaseStorage,
          notifications,
          storage,
          stripe,
        };
      }

      const {
        api,
        firebase,
        auth,
        functions,
        firebaseStorage,
        notifications,
        storage,
        stripe,
      } = health;

      if (isUnloading) return health;

      set({ serviceHealth: health, lastCheckAt: Date.now() });

      const allDown =
        !api &&
        !firebase &&
        !auth &&
        !functions &&
        !firebaseStorage &&
        !notifications &&
        !storage &&
        !stripe;
      if (allDown) {
        // All services down — likely no internet at all.
        // navigator.onLine is unreliable, so verify with an external ping.
        const hasInternet = await pingInternet();
        set({ status: hasInternet ? "services-unavailable" : "offline" });
      } else if (
        !api ||
        !firebase ||
        !auth ||
        !functions ||
        !firebaseStorage ||
        !notifications ||
        !storage ||
        !stripe
      ) {
        set({ status: "services-unavailable" });
      } else {
        set({ status: "online", failedRequestsCount: 0 });
      }

      return health;
    },

    checkConnection: async () => {
      const hasInternet = await pingInternet();

      if (isUnloading) return hasInternet;

      if (hasInternet) {
        // Internet works — now check if services are reachable
        await get().checkServices();
        return true;
      }

      set({ status: "offline", lastCheckAt: Date.now() });
      return false;
    },
  }))
);
